import {
  ai_apps_path,
  developers_mcp_path,
  new_user_session_path,
  pricing_path,
  privacy_path,
  root_path,
  start_path,
} from "@/routes"

/**
 * The public page inventory, in one place (INCO-395).
 *
 * The footer renders from this, and `spec/design/marketing_site_spec.rb` reads
 * it: every marketing route Rails declares has to appear here, or the build
 * fails. That is the point of keeping it as data rather than as markup — a page
 * added by INCO-396 or INCO-397 cannot ship with nothing linking to it, which
 * is how a marketing site ends up with pages only its author can find.
 *
 * Assembled rather than written out, to keep it off the obvious scrapes.
 */
export const SUPPORT_EMAIL = ["hello", "@", "incoapp", ".", "com"].join("")

export interface FooterLink {
  label: string
  href: string
  /** Off-app destinations — mailto, docs. Inertia's Link would try to fetch these. */
  external?: boolean
}

export interface FooterColumn {
  heading: string
  links: FooterLink[]
}

/**
 * The capability pages (INCO-396).
 *
 * Written out rather than derived from a route helper, because `/features/:slug`
 * is *one* parameterised route serving seven pages — the router cannot tell you
 * what the seven are. The catalogue that can is
 * `Marketing::Features::CATALOGUE`, and the design spec fails when this list and
 * that one disagree, in either direction.
 */
export const FEATURE_LINKS: FooterLink[] = [
  { label: "Offer intake", href: "/features/offer-intake" },
  { label: "Briefs", href: "/features/briefs" },
  { label: "Distribution", href: "/features/distribution" },
  { label: "Deals", href: "/features/deals" },
  { label: "Verified mandates", href: "/features/network" },
  { label: "Assistant", href: "/features/assistant" },
  { label: "AI apps", href: "/features/agents" },
]

/**
 * The solution pages (INCO-397). Written out for the same reason as
 * `FEATURE_LINKS`: `/solutions/:slug` is one parameterised route, so the router
 * cannot say what the six are. `Marketing::Solutions::CATALOGUE` can, and the
 * design spec fails when this list and that one disagree.
 */
export const SOLUTION_LINKS: FooterLink[] = [
  { label: "Metals", href: "/solutions/metals" },
  { label: "Agriculture", href: "/solutions/agriculture" },
  { label: "Fuels & petrochemicals", href: "/solutions/fuels-and-petrochemicals" },
  { label: "Independent brokers", href: "/solutions/independent-broker" },
  { label: "Brokerage desks", href: "/solutions/brokerage-desk" },
  { label: "Received a brief?", href: "/solutions/counterparty" },
]

export const FOOTER_COLUMNS: FooterColumn[] = [
  {
    heading: "Product",
    links: [
      { label: "Overview", href: root_path() },
      ...FEATURE_LINKS,
      // The front door for connected AI apps (INCO-378). Under Product rather
      // than Company: it is a capability somebody chooses, not a policy page.
      { label: "AI apps", href: ai_apps_path() },
    ],
  },
  {
    heading: "Solutions",
    links: SOLUTION_LINKS,
  },
  {
    heading: "Get started",
    links: [
      { label: "Pricing", href: pricing_path() },
      { label: "Start your desk", href: start_path() },
      { label: "Log in", href: new_user_session_path() },
    ],
  },
  {
    heading: "Company",
    links: [
      // The two a directory review asks for before it asks anything else
      // (INCO-370), and the two most likely to be looked for in a footer.
      { label: "Developers", href: developers_mcp_path() },
      { label: "Privacy", href: privacy_path() },
      { label: "Contact", href: `mailto:${SUPPORT_EMAIL}`, external: true },
    ],
  },
]
